import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';

import { GetSessionError } from '~/backend/connector-api/connector-components';
import ImageAndStructureSetDisplay from '~/components/InputDataPreview/ImageAndStructureSetDisplay';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { Modality } from '~/domain/dicom/modality';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { cutUID } from '~/utils/string';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import RegistrationModelSelection from './ContourPropagationModelSelection';

export type RegistrationTargetImage = {
    seriesId: string;
    modality: Modality;
    studyId?: string;
    label?: string;
};

type RegistrationModalProps = {
    targetImages: RegistrationTargetImage[];
    selectedTargetImage: RegistrationTargetImage | undefined;
    setSelectedTargetImage: (
        image: RegistrationTargetImage | undefined
    ) => void;
    availableModels: PredictionModel[];
    onSubmit: (
        selectedModel: PredictionModel,
        selectedStructures: string[],
        selectedCustomization?: string
    ) => void;
    isLoadingModels: boolean;
    isLoadingSession?: boolean;
    isSubmitting?: boolean;
    showPseudonymization: boolean;
    error: Error | GetSessionError | null;
};

export default function RegistrationModal({
    availableModels,
    onSubmit,
    targetImages,
    error,
    isLoadingModels,
    isLoadingSession,
    isSubmitting,
    selectedTargetImage,
    setSelectedTargetImage,
    showPseudonymization,
}: RegistrationModalProps) {
    const primaryImage = useRecoilValue(primaryImageState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    <FormattedMessage id="runRegistration" />
                </Typography>
                <Typography variant="body2">
                    <FormattedMessage id="runRegistrationDescription" />
                </Typography>
            </Box>
            <Box display="grid" gap={2}>
                <ImageAndStructureSetDisplay
                    image={primaryImage}
                    structureSet={currentStructureSet}
                    isLoading={isLoadingSession}
                    error={error}
                />
                <FormControl
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: !selectedTargetImage
                                    ? 'warning.main'
                                    : 'primary.main',
                            },
                            '&:hover fieldset': {
                                borderColor: !selectedTargetImage
                                    ? 'warning.dark'
                                    : 'primary.dark',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: !selectedTargetImage
                                    ? 'warning.dark'
                                    : 'primary.dark',
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: !selectedTargetImage
                                ? 'warning.main'
                                : 'primary.main',
                        },
                    }}
                >
                    <InputLabel
                        shrink
                        id="target-image-select-label"
                        size="small"
                    >
                        Target Image
                    </InputLabel>

                    <Select
                        notched
                        data-testid="adaptTargetImageSelect"
                        labelId="target-image-select-label"
                        id="target-image-select"
                        label={'Target Image'}
                        value={selectedTargetImage?.seriesId ?? ''}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) {
                                return (
                                    <Typography
                                        fontSize={14}
                                        color="text.secondary"
                                    >
                                        Select an image
                                    </Typography>
                                );
                            }
                            const selectedImage = targetImages?.find(
                                (img) => img.seriesId === selected
                            );
                            const hasLabel = !!selectedImage?.label;

                            return hasLabel ? (
                                <SensitiveInfoTypography>
                                    <Typography
                                        component="span"
                                        fontSize={14}
                                        color="white"
                                        fontStyle="normal"
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {selectedImage.label}
                                    </Typography>
                                </SensitiveInfoTypography>
                            ) : (
                                <Typography component="span">
                                    {selectedImage?.modality} -{' '}
                                    <SensitiveInfoTypography>
                                        {cutUID(selectedImage?.seriesId ?? '')}
                                    </SensitiveInfoTypography>
                                </Typography>
                            );
                        }}
                        onChange={(event) => {
                            const selected = targetImages?.find(
                                (img) => img.seriesId === event.target.value
                            );
                            setSelectedTargetImage(selected ?? undefined);
                        }}
                        size="small"
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    mt: 0.5,
                                },
                            },
                        }}
                    >
                        {targetImages?.map((image) => {
                            const hasLabel = !!image.label;
                            return (
                                <MenuItem
                                    key={image.seriesId!}
                                    value={image.seriesId!}
                                >
                                    <Box width="100%" overflow="hidden">
                                        <SensitiveInfoTypography>
                                            <Typography
                                                component="span"
                                                color="white"
                                                fontSize={hasLabel ? 14 : 13}
                                                fontStyle={
                                                    hasLabel
                                                        ? 'normal'
                                                        : 'italic'
                                                }
                                            >
                                                {hasLabel
                                                    ? image.label
                                                    : 'No series description'}
                                            </Typography>
                                        </SensitiveInfoTypography>
                                        <Box display="flex" gap={0.5}>
                                            <Typography
                                                variant="body2"
                                                fontSize={12}
                                                color={'textSecondary'}
                                            >
                                                {image.modality}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontSize={12}
                                                color="textSecondary"
                                            >
                                                -{' '}
                                                <SensitiveInfoTypography>
                                                    {cutUID(
                                                        image.seriesId,
                                                        4,
                                                        4
                                                    )}
                                                </SensitiveInfoTypography>{' '}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
                <RegistrationModelSelection
                    availableModels={availableModels}
                    onSubmit={onSubmit}
                    isLoadingModels={isLoadingModels}
                    disabled={isLoadingModels || !selectedTargetImage}
                    isSubmitting={isSubmitting}
                    showPseudonymization={showPseudonymization}
                />
            </Box>
        </Box>
    );
}
